import {
  defaultScoringConfig,
  runSimulation,
  type ScoringConfig,
  type SimulationConfig,
} from "./runSimulation";

export type GAConfig = {
  populationSize: number;
  generations: number;
  mutationRate: number;
  crossoverRate: number;
  eliteCount: number;
  fitnessWeights: {
    skillCorrelation: number;
    fairness: number;
    convergenceSpeed: number;
    predictionAccuracy: number;
    pointsCorrelation: number;
  };
  simConfig: Omit<SimulationConfig, "scoring">;
};

export const defaultGAConfig: GAConfig = {
  populationSize: 20,
  generations: 30,
  mutationRate: 0.15,
  crossoverRate: 0.7,
  eliteCount: 2,
  fitnessWeights: {
    skillCorrelation: 0.3,
    fairness: 0.2,
    convergenceSpeed: 0.2,
    predictionAccuracy: 0.15,
    pointsCorrelation: 0.15,
  },
  simConfig: {
    numPlayers: 8,
    numMatches: 80,
    teamSize: 1,
    numTeams: 2,
    trueSkillSpread: 8,
  },
};

export type GAIndividual = {
  config: ScoringConfig;
  fitness: number;
  metrics: {
    skillCorrelation: number;
    gini: number;
    convergenceMatch: number;
    predictionAccuracy: number;
    pointsCorrelation: number;
  };
};

export type GAGeneration = {
  generation: number;
  best: GAIndividual;
  avgFitness: number;
  population: GAIndividual[];
};

export type GAResult = {
  generations: GAGeneration[];
  bestConfig: ScoringConfig;
  bestFitness: number;
  bestMetrics: GAIndividual["metrics"];
};

// Mutable numeric keys we can evolve
const MUTABLE_KEYS: (keyof ScoringConfig)[] = [
  "scale",
  "clampMin",
  "clampMax",
  "upsetThreshold",
  "upsetMaxBonus",
  "streakMultiplier",
  "streakMaxBonus",
  "fatigueHalfLife",
  "momentumFactor",
  "momentumCap",
  "comebackThreshold",
  "comebackMaxBonus",
  "consistencySigmaThreshold",
  "consistencyBonus",
  "closenessMaxDelta",
  "closenessBonus",
  "activityWeight",
  "activityBeta",
];

const BOOLEAN_KEYS: (keyof ScoringConfig)[] = [
  "upsetEnabled",
  "streakEnabled",
  "fatigueEnabled",
  "momentumEnabled",
  "comebackEnabled",
  "consistencyEnabled",
  "closenessEnabled",
];

const BOUNDS: Record<string, [number, number]> = {
  scale: [5, 200],
  clampMin: [-500, 0],
  clampMax: [10, 500],
  upsetThreshold: [0.1, 0.5],
  upsetMaxBonus: [0, 1],
  streakMultiplier: [0, 0.3],
  streakMaxBonus: [0, 1],
  fatigueHalfLife: [5, 100],
  momentumFactor: [0, 0.2],
  momentumCap: [0, 1],
  comebackThreshold: [5, 50],
  comebackMaxBonus: [0, 1],
  consistencySigmaThreshold: [2, 15],
  consistencyBonus: [0, 0.5],
  closenessMaxDelta: [0.1, 3],
  closenessBonus: [0, 0.5],
  activityWeight: [0.1, 5],
  activityBeta: [1, 50],
};

const randomConfig = (): ScoringConfig => {
  const config = { ...defaultScoringConfig };
  for (const key of MUTABLE_KEYS) {
    const [min, max] = BOUNDS[key] ?? [0, 100];
    (config as any)[key] = min + Math.random() * (max - min);
  }
  for (const key of BOOLEAN_KEYS) {
    (config as any)[key] = Math.random() > 0.5;
  }
  return config;
};

const clampValue = (key: string, value: number): number => {
  const [min, max] = BOUNDS[key] ?? [0, 100];
  return Math.max(min, Math.min(max, value));
};

const evaluateFitness = (config: ScoringConfig, gaConfig: GAConfig): GAIndividual => {
  const simConfig: SimulationConfig = {
    ...gaConfig.simConfig,
    scoring: config,
  };

  const result = runSimulation(simConfig);
  const a = result.analytics;

  const metrics = {
    skillCorrelation: a.skillCorrelation,
    gini: a.giniCoefficient,
    convergenceMatch: a.convergenceMatch,
    predictionAccuracy: a.predictionAccuracy,
    pointsCorrelation: a.pointsCorrelation,
  };

  const w = gaConfig.fitnessWeights;
  const maxConvergence = gaConfig.simConfig.numMatches;

  const fitness =
    w.skillCorrelation * Math.max(0, metrics.skillCorrelation) +
    w.fairness * (1 - metrics.gini) +
    w.convergenceSpeed * (1 - metrics.convergenceMatch / maxConvergence) +
    w.predictionAccuracy * metrics.predictionAccuracy +
    w.pointsCorrelation * Math.max(0, metrics.pointsCorrelation);

  return { config, fitness, metrics };
};

const crossover = (a: ScoringConfig, b: ScoringConfig): ScoringConfig => {
  const child = { ...a };
  for (const key of MUTABLE_KEYS) {
    if (Math.random() > 0.5) {
      (child as any)[key] = (b as any)[key];
    }
  }
  for (const key of BOOLEAN_KEYS) {
    if (Math.random() > 0.5) {
      (child as any)[key] = (b as any)[key];
    }
  }
  return child;
};

const mutate = (config: ScoringConfig, rate: number): ScoringConfig => {
  const mutated = { ...config };
  for (const key of MUTABLE_KEYS) {
    if (Math.random() < rate) {
      const [min, max] = BOUNDS[key] ?? [0, 100];
      const range = max - min;
      const current = (mutated as any)[key] as number;
      const delta = (Math.random() - 0.5) * range * 0.3;
      (mutated as any)[key] = clampValue(key, current + delta);
    }
  }
  for (const key of BOOLEAN_KEYS) {
    if (Math.random() < rate * 0.5) {
      (mutated as any)[key] = !(mutated as any)[key];
    }
  }
  return mutated;
};

const tournamentSelect = (population: GAIndividual[], tournamentSize: number = 3): GAIndividual => {
  let best: GAIndividual | null = null;
  for (let i = 0; i < tournamentSize; i++) {
    const idx = Math.floor(Math.random() * population.length);
    const candidate = population[idx]!;
    if (!best || candidate.fitness > best.fitness) {
      best = candidate;
    }
  }
  return best!;
};

export const runGeneticAlgorithm = (
  gaConfig: GAConfig,
  onProgress?: (gen: GAGeneration) => void,
): GAResult => {
  const generations: GAGeneration[] = [];

  // Initialize population
  let population = Array.from({ length: gaConfig.populationSize }, () =>
    evaluateFitness(randomConfig(), gaConfig),
  );
  population.sort((a, b) => b.fitness - a.fitness);

  for (let g = 0; g < gaConfig.generations; g++) {
    const nextPop: GAIndividual[] = [];

    // Elitism
    for (let i = 0; i < gaConfig.eliteCount && i < population.length; i++) {
      nextPop.push(population[i]!);
    }

    // Breed
    while (nextPop.length < gaConfig.populationSize) {
      const parentA = tournamentSelect(population);
      const parentB = tournamentSelect(population);

      let childConfig: ScoringConfig;
      if (Math.random() < gaConfig.crossoverRate) {
        childConfig = crossover(parentA.config, parentB.config);
      } else {
        childConfig = { ...parentA.config };
      }

      childConfig = mutate(childConfig, gaConfig.mutationRate);
      const child = evaluateFitness(childConfig, gaConfig);
      nextPop.push(child);
    }

    population = nextPop.sort((a, b) => b.fitness - a.fitness);

    const avgFitness = population.reduce((s, i) => s + i.fitness, 0) / population.length;

    const gen: GAGeneration = {
      generation: g,
      best: population[0]!,
      avgFitness,
      population: [...population],
    };
    generations.push(gen);
    onProgress?.(gen);
  }

  const best = population[0]!;
  return {
    generations,
    bestConfig: best.config,
    bestFitness: best.fitness,
    bestMetrics: best.metrics,
  };
};
